import { OrderType } from 'graphql-api/gql-api-interfaces';

import {
  BusinessState,
  DataState,
  PriceType,
  TotalPrices,
} from '../../../models/config-strip-state.models';
import { getSelectedDeliveryPeriod } from '../../../shared/get-selected-delivery-period';
import { getPriceType, getProductPrices } from '../../../shared/shared';

export function calculateNewTotals({
  state,
  data,
  productId,
  difference,
  orderType,
}: {
  state: BusinessState;
  data: DataState;
  productId: string;
  difference: number;
  orderType: OrderType;
}): { quantity: number; prices: TotalPrices } {
  const initialTotals = { quantity: 0, prices: { wsp: 0, rrp: 0 } };

  let productSellPrices: TotalPrices;
  let productSellQuantity = 1; // sold by unit by default

  if (data.sizes[productId]) {
    productSellPrices = getSelectedSizePrices(
      state,
      data,
      productId,
      orderType,
    );
  } else {
    const prepack = data.prepacks[productId];
    if (!prepack) {
      return initialTotals;
    }

    const prepackTotals = prepack.quantities.reduce((t, p) => {
      const { wsp, rrp } = getSelectedSizePrices(
        state,
        data,
        productId,
        orderType,
      );
      return {
        quantity: t.quantity + p.quantity,
        prices: {
          wsp: t.prices.wsp + p.quantity * wsp,
          rrp: t.prices.rrp + p.quantity * rrp,
        },
      };
    }, initialTotals);

    productSellQuantity = prepackTotals.quantity;
    productSellPrices = prepackTotals.prices;
  }

  return {
    quantity: state.totalQuantity + productSellQuantity * difference,
    prices: {
      wsp: state.totalPrices.wsp + difference * productSellPrices.wsp,
      rrp: state.totalPrices.rrp + difference * productSellPrices.rrp,
    },
  };
}

function getSelectedSizePrices(
  state: BusinessState,
  data: DataState,
  productId: string,
  orderType: OrderType,
): TotalPrices {
  let sellPrice = 0;
  let rrpPrice = 0;
  if (data.product?.productId) {
    const priceType: PriceType = getPriceType(orderType);
    const prices = getProductPrices({
      productPrices: data.prices[priceType],
      articleId: data.product?.productId,
      variantId:
        data.sizes?.[productId]?.variantReference ??
        // if it's not size - then prepack. For prepacks we need to find any sizeId
        // and via sizeId find any related variant through `variantReference`
        data.sizes?.[data.prepacks?.[productId].quantities?.[0].productId]
          ?.variantReference,
      sizeId: productId,
      deliveryPeriod: getSelectedDeliveryPeriod(state),
    });
    sellPrice = prices.wspd?.min ?? prices.wsp?.min ?? 0;
    rrpPrice = prices.rrp?.min ?? prices.rrp?.min ?? 0;
  }
  return { wsp: sellPrice, rrp: rrpPrice };
}
